// @ts-nocheck
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { REACTION_TYPES } from '@/components/social-wall/ReactionPicker';

interface ReactionCount {
  reaction_type: string;
  count: number;
  percentage: number;
}

interface WeeklyTrend {
  week_start: string;
  count: number;
}

interface MostReactedPost {
  id: string;
  content: string;
  post_type: string;
  total_reactions: number;
  created_at: string;
}

interface ReactionAnalytics {
  receivedBreakdown: ReactionCount[];
  givenBreakdown: ReactionCount[];
  topReceivedVibe: ReactionCount | null;
  topGivenVibe: ReactionCount | null;
  mostReactedPost: MostReactedPost | null;
  weeklyTrend: WeeklyTrend[];
  totalReceived: number;
  totalGiven: number;
}

export const getVibeMessage = (reactionType: string): string => {
  const messages: Record<string, string> = {
    fire: "You bring the HYPE 🔥",
    trophy: "Elite status confirmed 🏆",
    grind: "You're known for the GRIND 💪",
    brain: "Big brain energy! 🧠",
    flowers: "Total respect 🌸",
    teammate: "Ultimate teammate 🤝",
    clutch: "Clutch player vibes 🎯",
    lockdown: "Lockdown defender 🧱",
    levelup: "Always leveling up! 🚀",
  };
  return messages[reactionType] || "You're getting noticed!";
};

export const useReactionAnalytics = (profileId?: string) => {
  const [analytics, setAnalytics] = useState<ReactionAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profileId) {
      setIsLoading(false);
      return;
    }

    const fetchAnalytics = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Get the date 30 days ago
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const thirtyDaysAgoStr = thirtyDaysAgo.toISOString();

        // Fetch reactions received (on user's posts)
        const { data: userPosts, error: postsError } = await supabase
          .from('wall_posts')
          .select('id')
          .eq('profile_id', profileId);

        if (postsError) throw postsError;

        const postIds = userPosts?.map(p => p.id) || [];

        let receivedBreakdown: ReactionCount[] = [];
        let totalReceived = 0;

        if (postIds.length > 0) {
          const { data: receivedData, error: receivedError } = await supabase
            .from('wall_post_reactions')
            .select('reaction_type')
            .in('post_id', postIds)
            .gte('created_at', thirtyDaysAgoStr);

          if (receivedError) throw receivedError;

          // Count by reaction type
          const receivedCounts: Record<string, number> = {};
          receivedData?.forEach(r => {
            receivedCounts[r.reaction_type] = (receivedCounts[r.reaction_type] || 0) + 1;
            totalReceived++;
          });

          receivedBreakdown = Object.entries(receivedCounts)
            .map(([reaction_type, count]) => ({
              reaction_type,
              count,
              percentage: totalReceived > 0 ? Math.round((count / totalReceived) * 100) : 0
            }))
            .sort((a, b) => b.count - a.count);
        }

        // Fetch reactions given (by user)
        const { data: givenData, error: givenError } = await supabase
          .from('wall_post_reactions')
          .select('reaction_type')
          .eq('profile_id', profileId)
          .gte('created_at', thirtyDaysAgoStr);

        if (givenError) throw givenError;

        const givenCounts: Record<string, number> = {};
        let totalGiven = 0;
        givenData?.forEach(r => {
          givenCounts[r.reaction_type] = (givenCounts[r.reaction_type] || 0) + 1;
          totalGiven++;
        });

        const givenBreakdown = Object.entries(givenCounts)
          .map(([reaction_type, count]) => ({
            reaction_type,
            count,
            percentage: totalGiven > 0 ? Math.round((count / totalGiven) * 100) : 0
          }))
          .sort((a, b) => b.count - a.count);

        // Most reacted post
        let mostReactedPost: MostReactedPost | null = null;
        if (postIds.length > 0) {
          const { data: topPost, error: topPostError } = await supabase
            .from('wall_posts')
            .select('id, content, post_type, reactions_count, created_at')
            .eq('profile_id', profileId)
            .order('created_at', { ascending: false })
            .limit(50);

          if (!topPostError && topPost) {
            // Calculate total reactions for each post
            const postsWithTotal = topPost.map(p => {
              const counts = p.reactions_count as Record<string, number> || {};
              const total = Object.values(counts).reduce((sum, c) => sum + (c || 0), 0);
              return { ...p, total_reactions: total };
            });

            const sorted = postsWithTotal.sort((a, b) => b.total_reactions - a.total_reactions);
            if (sorted.length > 0 && sorted[0].total_reactions > 0) {
              mostReactedPost = {
                id: sorted[0].id,
                content: sorted[0].content || '',
                post_type: sorted[0].post_type,
                total_reactions: sorted[0].total_reactions,
                created_at: sorted[0].created_at
              };
            }
          }
        }

        // Weekly trend (last 4 weeks)
        const weeklyTrend: WeeklyTrend[] = [];
        for (let i = 3; i >= 0; i--) {
          const weekStart = new Date();
          weekStart.setDate(weekStart.getDate() - (i * 7) - weekStart.getDay());
          weekStart.setHours(0, 0, 0, 0);
          
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 7);

          if (postIds.length > 0) {
            const { count, error: weekError } = await supabase
              .from('wall_post_reactions')
              .select('*', { count: 'exact', head: true })
              .in('post_id', postIds)
              .gte('created_at', weekStart.toISOString())
              .lt('created_at', weekEnd.toISOString());

            if (!weekError) {
              weeklyTrend.push({
                week_start: weekStart.toISOString().split('T')[0],
                count: count || 0
              });
            }
          } else {
            weeklyTrend.push({
              week_start: weekStart.toISOString().split('T')[0],
              count: 0
            });
          }
        }

        setAnalytics({
          receivedBreakdown,
          givenBreakdown,
          topReceivedVibe: receivedBreakdown[0] || null,
          topGivenVibe: givenBreakdown[0] || null,
          mostReactedPost,
          weeklyTrend,
          totalReceived,
          totalGiven
        });
      } catch (err) {
        console.error('Error fetching reaction analytics:', err);
        setError('Failed to load analytics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [profileId]);

  return { analytics, isLoading, error };
};
